import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import useSWR from "swr";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import classNames from "classnames";
import { Entity } from "models/entity";
import { useToggle } from "usehooks-ts";

import CustomCard from "components/CustomCard";
import EntityAnalyticsChart from "components/EntityAnalyticsChart";
import { entitySmartScoreUrl } from "components/EntitySmartScore";
import EntityStatsCard from "components/EntityStatsCard";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import publicUserPriceChart from "assets/public-user-price-chart.svg";

import style from "./style.module.scss";

export const ALWAYS_ENABLE_PRICE_CHART = false;

const EntityDataModal = dynamic(() => import("components/EntityDataModal"));

type EntityAnalyticsWidgetProps = {
  entity: Entity;
  showEntityStats?: boolean;
};

const EntityAnalyticsWidget = ({
  entity,
  showEntityStats,
}: EntityAnalyticsWidgetProps) => {
  const { authenticated } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const { t } = useTranslation();

  const [showEntityData, toggleEntityData] = useToggle();
  const [showEntityAnalytics, setShowEntityAnalytics] = useState(true);

  const { data: entitySmartScore } = useSWR(
    entitySmartScoreUrl(entity.slug),
    baseFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (!entity) return null;

  if (!showEntityAnalytics) return null;

  if (authenticated || ALWAYS_ENABLE_PRICE_CHART) {
    return (
      <CustomCard className="px-[15px] py-[15px]">
        <div className="mb-[10px] flex items-center justify-between">
          <h1 className="text-[16px] hover:text-smart-teal mb-0">
            <Link href={`/entities/${entity.slug}/research`}>
              {entity.prettyName}
            </Link>
          </h1>
          {entitySmartScore?.countryAndSectorScore && (
            <span className="text-xl font-semibold text-utah-sky">
              {entitySmartScore?.countryAndSectorScore}/5
            </span>
          )}
        </div>
        <EntityAnalyticsChart
          entity={entity}
          onEmpty={() => setShowEntityAnalytics(false)}
        />
        {showEntityStats && authenticated && (
          <EntityStatsCard entity={entity} full />
        )}
        <div className="px-[5px] mt-[5px] ">
          {authenticated ? (
            <>
              <div
                className="text-[14px] font-bold text-smart-teal hover:text-dark-teal cursor-pointer"
                onClick={toggleEntityData}
              >
                View all Data »
              </div>
              <EntityDataModal
                slug={entity?.slug}
                isOpen={showEntityData}
                onClose={toggleEntityData}
              />
            </>
          ) : (
            <Link
              href={`/entities/${entity.slug}/analytics`}
              className="text-[14px] font-bold text-smart-teal hover:text-dark-teal"
            >
              {"View More »"}
            </Link>
          )}
        </div>
      </CustomCard>
    );
  }

  return (
    <CustomCard additionalStyle={{ padding: 10 }}>
      <div className={style.container}>
        <div className={classNames(style.header)}>
          <span className="font-bold">{t("Price Chart")}</span>
          <span
            className="ml-[5px] font-normal text-[#b5485d] hover:text-[#b5485d] cursor-pointer"
            onClick={() => toggleModal("sign-up")}
          >
            ({t("Sign Up to Access")})
          </span>
        </div>
        <div className="p-[5px]">
          <Image src={publicUserPriceChart} alt="analytics-chart" width={270} />
        </div>
      </div>
    </CustomCard>
  );
};

export default EntityAnalyticsWidget;
